import { useEffect, useLayoutEffect, useState } from 'react';
let trackerCount = 0;
let pointerIsDown = false;
const markPointerDown = () => {
    pointerIsDown = true;
};
const markPointerUp = () => {
    pointerIsDown = false;
};
/** A press released outside the window never delivers pointerup. The next move with no buttons down reports that. */
const markPointerMove = (event) => {
    if (event.buttons === 0) {
        pointerIsDown = false;
    }
};
const markPointerInactive = () => {
    pointerIsDown = false;
};
const markVisibility = () => {
    if (document.hidden) {
        pointerIsDown = false;
    }
};
const trackPointer = () => {
    if (typeof document === 'undefined') {
        return () => undefined;
    }
    if (trackerCount === 0) {
        document.addEventListener('pointerdown', markPointerDown, true);
        document.addEventListener('pointerup', markPointerUp, true);
        document.addEventListener('pointercancel', markPointerUp, true);
        document.addEventListener('pointermove', markPointerMove, true);
        document.addEventListener('visibilitychange', markVisibility);
        window.addEventListener('blur', markPointerInactive);
    }
    trackerCount += 1;
    return () => {
        trackerCount -= 1;
        if (trackerCount === 0) {
            document.removeEventListener('pointerdown', markPointerDown, true);
            document.removeEventListener('pointerup', markPointerUp, true);
            document.removeEventListener('pointercancel', markPointerUp, true);
            document.removeEventListener('pointermove', markPointerMove, true);
            document.removeEventListener('visibilitychange', markVisibility);
            window.removeEventListener('blur', markPointerInactive);
            pointerIsDown = false;
        }
    };
};
/**
 * Reports whether the sheet is opening under a pointer that is still pressed.
 *
 * Headless UI opens Menu and Listbox on `pointerdown`, then watches the matching `pointerup` on the document: a
 * release over an item or option after more than 200ms counts as choosing it (`useQuickRelease`). The sheet slides
 * over the trigger, so with a mouse the release lands on whichever row sits above the trigger, and the sheet closes
 * as soon as it opened. While this returns true the sheet and backdrop should ignore pointer events, so the release
 * falls through to the trigger underneath. Touch pointers release on the element they pressed and are unaffected.
 * A release outside the window never delivers `pointerup`, so the tracker also clears on blur, when the document
 * is hidden, and on a later `pointermove` with `buttons === 0`. The open sheet lifts its guard on those too.
 */
export const useOptionsSheetPressGuard = (isOpen) => {
    const [isGuarded, setIsGuarded] = useState(false);
    useEffect(trackPointer, []);
    useLayoutEffect(() => {
        if (!isOpen || !pointerIsDown) {
            setIsGuarded(false);
            return undefined;
        }
        setIsGuarded(true);
        let releaseTimeout;
        // Headless UI re-subscribes its quick-release `pointerup` listener on every render. A state update inside this
        // dispatch would re-render before that listener runs and it would miss the release, so the guard lifts in a
        // later task.
        const release = () => {
            releaseTimeout !== null && releaseTimeout !== void 0 ? releaseTimeout : (releaseTimeout = setTimeout(() => setIsGuarded(false), 0));
        };
        const releaseIfIdle = (event) => {
            if (event.buttons === 0) {
                release();
            }
        };
        const releaseIfHidden = () => {
            if (document.hidden) {
                release();
            }
        };
        document.addEventListener('pointerup', release, true);
        document.addEventListener('pointercancel', release, true);
        document.addEventListener('pointermove', releaseIfIdle, true);
        document.addEventListener('visibilitychange', releaseIfHidden);
        window.addEventListener('blur', release);
        return () => {
            clearTimeout(releaseTimeout);
            document.removeEventListener('pointerup', release, true);
            document.removeEventListener('pointercancel', release, true);
            document.removeEventListener('pointermove', releaseIfIdle, true);
            document.removeEventListener('visibilitychange', releaseIfHidden);
            window.removeEventListener('blur', release);
        };
    }, [isOpen]);
    return isGuarded;
};
/** Test-only. Forgets a press the tracker saw before the sheet under test mounted. */
export const resetOptionsSheetPressGuardForTests = () => {
    pointerIsDown = false;
};
//# sourceMappingURL=use_options_sheet_press_guard.hook.js.map